import { useState } from "react";
import type { FormEvent } from "react";
import Layout from "@/components/feature/Layout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db9n2ek6oi3g3tv8v15g";

const contactChannels = [
  { icon: "ri-mail-line", label: "官方邮箱", value: "hello@kfilm-archive.example" },
  { icon: "ri-wechat-line", label: "微信公众号", value: "韩影资料馆" },
  { icon: "ri-weibo-line", label: "微博", value: "@韩国伦理电影在线观看" },
  { icon: "ri-time-line", label: "回复时间", value: "工作日 10:00 - 18:00" },
];

const topics = [
  {
    icon: "ri-file-edit-line",
    title: "影片纠错",
    desc: "发现导演、主演或片长信息有误，欢迎附上影片名称与正确资料。",
  },
  {
    icon: "ri-lightbulb-line",
    title: "片单建议",
    desc: "想看某个主题的专题，例如「家庭叙事」「海边电影」，可以告诉我们。",
  },
  {
    icon: "ri-hand-heart-line",
    title: "内容合作",
    desc: "影评投稿、影展资讯与合作咨询，请附上可联系的方式与简介。",
  },
];

interface FormState {
  status: "idle" | "submitting" | "success" | "error";
  message: string;
}

export default function ContactPage() {
  const [form, setForm] = useState<FormState>({ status: "idle", message: "" });

  usePageMeta({
    title: "联系我们 - 韩国伦理电影在线观看",
    description:
      "联系韩国伦理电影在线观看编辑团队，提交影片资料纠错、片单建议或内容合作咨询。可通过站内表单或官方邮箱与我们取得联系。",
    keywords: "联系韩国伦理电影在线观看,韩国伦理电影,影片纠错,内容合作,韩影资料",
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const element = event.currentTarget;
    const formData = new FormData(element);

    const honeypot = String(formData.get("contact_alt") ?? "").trim();
    if (honeypot) {
      setForm({ status: "success", message: "留言已收到，我们会尽快回复你。" });
      element.reset();
      return;
    }

    const name = String(formData.get("name") ?? "").trim();
    const email = String(formData.get("email") ?? "").trim();
    const message = String(formData.get("message") ?? "").trim();

    if (name.length < 2) {
      setForm({ status: "error", message: "请填写至少 2 个字符的姓名或昵称。" });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setForm({ status: "error", message: "请填写有效的邮箱地址，方便我们回复你。" });
      return;
    }
    if (message.length < 10) {
      setForm({ status: "error", message: "留言内容请至少填写 10 个字符。" });
      return;
    }
    if (message.length > 500) {
      setForm({ status: "error", message: "留言内容不能超过 500 个字符。" });
      return;
    }

    setForm({ status: "submitting", message: "" });

    const payload = new URLSearchParams();
    payload.append("name", name);
    payload.append("email", email);
    payload.append("message", message);

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText) as typeof parsed;
      } catch {
        parsed = null;
      }

      const serverMessage =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSuccess = response.ok && parsed?.code === "OK";

      if (isSuccess) {
        setForm({ status: "success", message: "留言已提交，感谢你的反馈，我们会尽快回复。" });
        element.reset();
      } else {
        setForm({
          status: "error",
          message: serverMessage
            ? `提交失败：${serverMessage}`
            : "提交没有成功，请稍后再试一次。",
        });
      }
    } catch {
      setForm({
        status: "error",
        message: "网络连接出现问题，留言未能发送。请检查网络后重试，或直接发送邮件给我们。",
      });
    }
  };

  return (
    <Layout>
      <div className="bg-background-100/60 border-b border-background-200/70">
        <div className="px-4 md:px-8 lg:px-12 pt-8 pb-10">
          <div className="max-w-shell mx-auto">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "联系我们" }]} />
            <p className="mt-5 font-label text-xs tracking-[0.32em] text-primary-500 mb-2">CONTACT</p>
            <h1 className="font-heading text-2xl md:text-4xl text-foreground-950 font-bold">联系我们</h1>
            <p className="mt-3 text-sm text-foreground-700 leading-relaxed max-w-2xl">
              影片资料纠错、片单建议或内容合作，都可以通过下面的表单告诉我们。
              我们会认真阅读每一条留言，并在工作日尽量回复。
            </p>
          </div>
        </div>
      </div>

      <div className="px-4 md:px-8 lg:px-12 py-10 md:py-14">
        <div className="max-w-shell mx-auto grid lg:grid-cols-[minmax(0,1fr)_340px] gap-8 lg:gap-10">
          <section className="rounded-lg border border-background-200/70 bg-background-50 p-5 md:p-7">
            <h2 className="font-heading text-lg md:text-xl text-foreground-950 font-semibold">给我们留言</h2>
            <p className="mt-1.5 text-xs text-foreground-500">
              带 <span className="text-primary-500">*</span> 的字段为必填项，留言不超过 500 字。
            </p>

            <form
              data-readdy-form
              id="contact-form"
              onSubmit={handleSubmit}
              className="mt-6 space-y-4"
              noValidate
            >
              <div className="grid sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="block text-xs text-foreground-600 mb-1.5">
                    姓名 / 昵称 <span className="text-primary-500">*</span>
                  </span>
                  <input
                    type="text"
                    name="name"
                    required
                    maxLength={40}
                    placeholder="怎么称呼你"
                    className="w-full h-11 px-3.5 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:border-primary-400 transition-colors"
                  />
                </label>
                <label className="block">
                  <span className="block text-xs text-foreground-600 mb-1.5">
                    邮箱 <span className="text-primary-500">*</span>
                  </span>
                  <input
                    type="email"
                    name="email"
                    required
                    maxLength={80}
                    placeholder="用于接收回复"
                    className="w-full h-11 px-3.5 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:border-primary-400 transition-colors"
                  />
                </label>
              </div>

              <label className="block">
                <span className="block text-xs text-foreground-600 mb-1.5">
                  留言内容 <span className="text-primary-500">*</span>
                </span>
                <textarea
                  name="message"
                  required
                  rows={6}
                  maxLength={500}
                  placeholder="想反馈的影片资料、建议的专题，或合作意向都可以写在这里（最多 500 字）"
                  className="w-full px-3.5 py-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:border-primary-400 transition-colors resize-none leading-relaxed"
                ></textarea>
              </label>

              <label className="contact-extra-field" aria-hidden="true">
                公司电话
                <input type="text" name="contact_alt" tabIndex={-1} autoComplete="off" readOnly />
              </label>

              <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-1">
                <button
                  type="submit"
                  disabled={form.status === "submitting"}
                  className={`h-11 px-6 rounded-md text-sm font-medium flex items-center justify-center gap-2 whitespace-nowrap transition-colors ${
                    form.status === "submitting"
                      ? "bg-background-300 text-foreground-500 cursor-not-allowed"
                      : "bg-primary-500 text-background-50 hover:bg-primary-600 cursor-pointer"
                  }`}
                >
                  {form.status === "submitting" ? (
                    <>
                      <span className="w-4 h-4 rounded-full border-2 border-background-50/40 border-t-background-50 animate-spin"></span>
                      提交中…
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line" aria-hidden="true"></i>
                      提交留言
                    </>
                  )}
                </button>
                <p className="text-[11px] text-foreground-500 leading-relaxed">
                  提交即表示你同意我们仅将信息用于回复本次咨询。
                </p>
              </div>

              {form.status === "success" && (
                <div className="flex items-start gap-2.5 rounded-md border border-primary-300/50 bg-primary-100/40 px-4 py-3">
                  <i className="ri-checkbox-circle-line text-primary-500 mt-0.5" aria-hidden="true"></i>
                  <p className="text-sm text-foreground-900 leading-relaxed">{form.message}</p>
                </div>
              )}
              {form.status === "error" && (
                <div className="flex items-start gap-2.5 rounded-md border border-accent-500/40 bg-accent-100/30 px-4 py-3">
                  <i className="ri-error-warning-line text-accent-500 mt-0.5" aria-hidden="true"></i>
                  <p className="text-sm text-foreground-900 leading-relaxed">{form.message}</p>
                </div>
              )}
            </form>
          </section>

          <aside className="space-y-4">
            <div className="rounded-lg border border-background-200/70 bg-background-100/50 p-5">
              <h2 className="font-heading text-base text-foreground-950 font-semibold mb-4">联系方式</h2>
              <ul className="space-y-3.5">
                {contactChannels.map((item) => (
                  <li key={item.label} className="flex items-start gap-3">
                    <span className="w-8 h-8 rounded-md bg-secondary-100 text-secondary-900 flex items-center justify-center shrink-0">
                      <i className={`${item.icon} text-sm`} aria-hidden="true"></i>
                    </span>
                    <div className="min-w-0">
                      <p className="text-[11px] text-foreground-500">{item.label}</p>
                      <p className="text-sm text-foreground-900 mt-0.5 break-words">{item.value}</p>
                    </div>
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-[11px] text-foreground-500 leading-relaxed">
                以上邮箱与账号为演示信息，表单提交会通过站内表单服务记录。
              </p>
            </div>

            <div className="rounded-lg border border-background-200/70 bg-background-100/50 p-5">
              <h2 className="font-heading text-base text-foreground-950 font-semibold mb-3">可以聊什么</h2>
              <ul className="space-y-3.5">
                {topics.map((topic) => (
                  <li key={topic.title}>
                    <p className="text-sm text-foreground-900 flex items-center gap-2">
                      <i className={`${topic.icon} text-primary-500`} aria-hidden="true"></i>
                      {topic.title}
                    </p>
                    <p className="mt-1 text-xs leading-relaxed text-foreground-600 pl-6">{topic.desc}</p>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg border border-secondary-200/50 bg-secondary-100/40 p-5">
              <p className="text-xs leading-relaxed text-secondary-900">
                本站不提供影片播放、下载或资源索取服务，相关问题恕不回复。请支持正版影视内容。
              </p>
            </div>
          </aside>
        </div>
      </div>
    </Layout>
  );
}